import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

function Stat({ label, value, testId }) {
  return (
    <div className="ns-card p-5" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}

function RankList({ title, rows, label, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="ns-card p-6" data-testid={testId}>
      <div className="font-serif text-xl mb-4">{title}</div>
      {rows.length === 0 && <div className="text-sm text-slate-400 italic">Nothing yet.</div>}
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} data-testid={`${testId}-row-${i}`}>
            <div className="flex justify-between text-sm"><span className="truncate pr-3">{label(r)}</span><span className="font-mono text-xs text-slate-600">{r.count} · {gbp(r.cost)}</span></div>
            <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-rose-500" style={{ width: `${(r.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DamageTrends() {
  const [months, setMonths] = useState(12);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/damage-reports/trends", { params: { months } }).then((r) => setD(r.data)).catch(() => {}); }, [months]);
  if (!d) return <div className="text-sm text-slate-400">Loading trends…</div>;
  return (
    <div className="space-y-6" data-testid="damage-trends">
      <div className="flex gap-2">
        {[3, 6, 12, 24].map((m) => (
          <button key={m} onClick={() => setMonths(m)} data-testid={`damage-trends-months-${m}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${months === m ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}>{m} months</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Reports" value={d.totals.reports} testId="damage-trends-reports" />
        <Stat label="Damage cost" value={gbp(d.totals.cost)} testId="damage-trends-cost" />
        <Stat label="Charged to guests" value={gbp(d.totals.charged)} testId="damage-trends-charged" />
        <Stat label="Recovered" value={`${d.totals.recovered_pct}%`} testId="damage-trends-recovered" />
      </div>
      <div className="ns-card p-6" data-testid="damage-trends-monthly">
        <div className="font-serif text-xl mb-4">Reports per month</div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(m) => m.slice(2)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
              <Tooltip formatter={(v, n) => (n === "cost" ? gbp(v) : v)} />
              <Bar dataKey="count" fill="#e11d48" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <RankList title="Most damaged properties" rows={d.by_property} label={(r) => r.property_name || "Unknown"} testId="damage-trends-properties" />
        <RankList title="Most damaged items" rows={d.by_item} label={(r) => r.label} testId="damage-trends-items" />
      </div>
    </div>
  );
}
